/**
 * "Install the app" — the portal as a real application.
 *
 * The portal is already everything an app needs to be (its own icon, its own
 * window, native notifications through the push worker); the only thing
 * missing was somebody telling the team it can be installed. This file is
 * that telling, in two shapes that share one source of truth (`useInstall`):
 *
 *   · `<InstallApp>`  — a card that floats at the right edge for people still
 *     using the portal in a browser tab. It says what installing buys, warns
 *     that the browser will ask to confirm (the step where people give up),
 *     and leaves with one tap. It NEVER appears once the app is installed.
 *   · `<InstallHowTo>` — the three manual steps, for Safari, which has no
 *     install API and is exactly where people fail to install a PWA.
 *
 * "Not now" hides the card for THIS visit only (sessionStorage): it comes back
 * next visit, and keeps coming back until the app is really installed. Anyone
 * who wants it sooner has the permanent entry in their Profile.
 * Ticking «Don't show for 30 days» before closing keeps it away for a month
 * in this browser (Gerónimo, 28 Oct 2026: «para que no le aparezca todo el
 * tiempo que entra al portal»); then it comes back.
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Lang } from '@/data/types';
import { useInstall } from '@/hooks/useInstall';

const DISMISS_KEY = 'lexidy-pwa-later';
const SNOOZE_KEY = 'lexidy-pwa-snooze-until';
export const SNOOZE_DAYS = 30;

/** Still inside the 30 days somebody asked for? `raw` is the stored epoch ms. */
export function isSnoozed(raw: string | null, now: number): boolean {
  const until = Number(raw);
  return Number.isFinite(until) && until > now;
}

const T = (lang: Lang, en: string, es: string, fr: string, pt: string) =>
  lang === 'es' ? es : lang === 'fr' ? fr : lang === 'pt' ? pt : en;

/** Safari's three taps — reused by the card and by the Profile panel. */
export function InstallHowTo({ lang, onClose }: { lang: Lang; onClose: () => void }) {
  return createPortal(
    <div className="svc-modal-backdrop" onClick={onClose}>
      <div className="svc-modal install-how" onClick={(e) => e.stopPropagation()}>
        <button className="svc-modal-close" onClick={onClose} aria-label="close">×</button>
        <div className="svc-modal-body">
          <h2 className="install-how-h2">{T(lang, 'Install the app', 'Instalar la app', 'Installer l’app', 'Instalar a app')}</h2>
          <p className="install-how-p">
            {T(lang,
              'Safari has no install button, so it takes three taps — and on iPhone this is also what switches notifications on.',
              'Safari no tiene botón de instalar, así que son tres toques — y en iPhone es además lo que activa las notificaciones.',
              'Safari n’a pas de bouton d’installation : trois gestes suffisent — et sur iPhone c’est aussi ce qui active les notifications.',
              'O Safari não tem botão de instalar: são três toques — e no iPhone é também o que ativa as notificações.')}
          </p>
          <ol className="install-how-steps">
            <li>{T(lang, 'Open the Share menu', 'Abre el menú Compartir', 'Ouvrez le menu Partager', 'Abra o menu Partilhar')}</li>
            <li>{T(lang, 'Choose “Add to Home Screen”', 'Elige «Añadir a pantalla de inicio»', 'Choisissez « Sur l’écran d’accueil »', 'Escolha «Adicionar ao ecrã principal»')}</li>
            <li>{T(lang, 'Confirm — the portal appears as an app', 'Confirma — el portal aparece como una app', 'Confirmez — le portail devient une app', 'Confirme — o portal fica como app')}</li>
          </ol>
          <p className="install-how-foot">
            {T(lang,
              'On a Mac, use Chrome or Edge instead and it installs in one click.',
              'En Mac, usa Chrome o Edge y se instala de un clic.',
              'Sur Mac, utilisez Chrome ou Edge : un clic suffit.',
              'No Mac, use Chrome ou Edge: instala com um clique.')}
          </p>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function InstallApp({ lang }: { lang: Lang }) {
  const { state, refused, checked, install } = useInstall();
  const [howTo, setHowTo] = useState(false);
  /* Dismissed for THIS visit only. The card is meant to come back until the
     app is actually installed — that is what was asked for — but coming back
     three times in one afternoon is nagging, not helping. sessionStorage
     forgets the moment the tab closes. */
  const [hidden, setHidden] = useState(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === '1' || isSnoozed(localStorage.getItem(SNOOZE_KEY), Date.now());
    } catch { return false; }
  });
  const [snooze, setSnooze] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    /* Never on the first paint: the portal has work to show, and a card that
       lands on top of it before it has drawn reads as an ad. */
    const t = setTimeout(() => setReady(true), 6000);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    setHidden(true);
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
      if (snooze) localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_DAYS * 86_400_000));
    } catch { /* private mode */ }
  };

  // Installed, dismissed, still checking, too early, or a browser that can
  // neither install nor be told how: silence.
  const offer = state === 'available' || state === 'manual' || state === 'declined';
  if (hidden || !ready || !checked || !offer) {
    return howTo ? <InstallHowTo lang={lang} onClose={() => setHowTo(false)} /> : null;
  }
  const safari = state === 'manual';

  return (
    <>
      {createPortal(
        <aside className="pwa-card" role="dialog" aria-label={T(lang, 'Install the app', 'Instalar la app', 'Installer l’app', 'Instalar a app')}>
          <button className="pwa-x" onClick={dismiss} aria-label={T(lang, 'Not now', 'Ahora no', 'Plus tard', 'Agora não')}>×</button>
          <div className="pwa-head">
            <img src="/icon-192.png" alt="" className="pwa-icon" width={40} height={40} />
            <div className="pwa-title">
              <strong>{T(lang, 'Install the portal', 'Instala el portal', 'Installez le portail', 'Instale o portal')}</strong>
              <span>{T(lang, 'as a desktop app', 'como app de escritorio', 'comme application', 'como app de secretária')}</span>
            </div>
          </div>
          <ul className="pwa-why">
            <li>{T(lang, 'Its own window and icon — no browser tabs', 'Ventana e icono propios — sin pestañas', 'Fenêtre et icône propres — sans onglets', 'Janela e ícone próprios — sem separadores')}</li>
            <li>{T(lang, 'Notifications reach you with the portal closed', 'Las notificaciones te llegan con el portal cerrado', 'Les notifications arrivent portail fermé', 'As notificações chegam com o portal fechado')}</li>
            <li>{T(lang, 'Always up to date — nothing to update by hand', 'Siempre al día — nada que actualizar a mano', 'Toujours à jour — rien à mettre à jour', 'Sempre atualizado — nada para atualizar')}</li>
          </ul>
          {refused ? (
            <p className="pwa-hint">
              {T(lang,
                'No problem. You can install it any time from the ⊕ icon at the right of the address bar, or from your Profile.',
                'Sin problema. Puedes instalarla cuando quieras desde el icono ⊕ a la derecha de la barra de direcciones, o desde tu Perfil.',
                'Pas de souci. Vous pourrez l’installer depuis l’icône ⊕ à droite de la barre d’adresse, ou depuis votre Profil.',
                'Sem problema. Pode instalá-la a partir do ícone ⊕ à direita da barra de endereço, ou no seu Perfil.')}
            </p>
          ) : (
            <p className="pwa-hint">
              {safari
                ? T(lang,
                    'Safari installs it by hand — three taps, and on iPhone this is also what turns notifications on.',
                    'Safari la instala a mano — tres toques, y en iPhone es además lo que activa las notificaciones.',
                    'Safari l’installe à la main — trois gestes, et sur iPhone cela active aussi les notifications.',
                    'O Safari instala à mão — três toques, e no iPhone é o que ativa as notificações.')
                : T(lang,
                    'Your browser will ask to confirm: press Install.',
                    'El navegador te pedirá confirmar: pulsa Instalar.',
                    'Le navigateur demandera confirmation : appuyez sur Installer.',
                    'O navegador vai pedir confirmação: carregue em Instalar.')}
            </p>
          )}
          <label className="pwa-snooze">
            <input type="checkbox" checked={snooze} onChange={(e) => setSnooze(e.target.checked)} />
            {T(lang, 'Don’t show for 30 days', 'No mostrar en 30 días', 'Ne plus afficher pendant 30 jours', 'Não mostrar durante 30 dias')}
          </label>
          <div className="pwa-acts">
            <button className="pwa-later" onClick={dismiss}>
              {T(lang, 'Not now', 'Ahora no', 'Plus tard', 'Agora não')}
            </button>
            <button
              className="pwa-go"
              onClick={() => { if (safari) setHowTo(true); else void install(); }}
            >
              {safari
                ? T(lang, 'How to install', 'Cómo instalarla', 'Comment l’installer', 'Como instalar')
                : T(lang, 'Install', 'Instalar', 'Installer', 'Instalar')}
            </button>
          </div>
        </aside>,
        document.body,
      )}
      {howTo && <InstallHowTo lang={lang} onClose={() => setHowTo(false)} />}
    </>
  );
}
